import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/shared/ui/badge'
import { Checkbox } from '@/shared/ui/checkbox'
import { Skeleton } from '@/shared/ui/skeleton'

interface CheckboxListProps {
  items: { id: string; label: string }[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
}

interface InfiniteCheckboxListProps extends CheckboxListProps {
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export function CheckboxListSkeleton() {
  return (
    <div className="rounded-md border border-zinc-200 divide-y divide-zinc-100">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  )
}

export function CheckboxList({ items, value, onChange, placeholder }: CheckboxListProps) {
  const { t } = useTranslation()

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }

  if (items.length === 0) {
    return <p className="text-sm text-zinc-400 py-2">{placeholder ?? t('common.noData')}</p>
  }

  return (
    <div className="rounded-md border border-zinc-200 max-h-52 overflow-y-auto divide-y divide-zinc-100">
      {items.map((item) => (
        <label
          key={item.id}
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50 transition-colors"
        >
          <Checkbox checked={value.includes(item.id)} onCheckedChange={() => toggle(item.id)} />
          <span className="text-sm text-zinc-700">{item.label}</span>
          {value.includes(item.id) && (
            <Badge variant="secondary" className="ml-auto text-xs">
              Đã chọn
            </Badge>
          )}
        </label>
      ))}
    </div>
  )
}

export function InfiniteCheckboxList({
  items,
  value,
  onChange,
  placeholder,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: InfiniteCheckboxListProps) {
  const { t } = useTranslation()
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }

  if (items.length === 0 && !isFetchingNextPage) {
    return <p className="text-sm text-zinc-400 py-2">{placeholder ?? t('common.noData')}</p>
  }

  return (
    <div className="rounded-md border border-zinc-200 max-h-52 overflow-y-auto divide-y divide-zinc-100">
      {items.map((item) => (
        <label
          key={item.id}
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50 transition-colors"
        >
          <Checkbox checked={value.includes(item.id)} onCheckedChange={() => toggle(item.id)} />
          <span className="text-sm text-zinc-700">{item.label}</span>
          {value.includes(item.id) && (
            <Badge variant="secondary" className="ml-auto text-xs">
              Đã chọn
            </Badge>
          )}
        </label>
      ))}
      <div ref={sentinelRef} className="px-3 py-2">
        {isFetchingNextPage && <p className="text-xs text-zinc-400">{t('common.loading')}</p>}
      </div>
    </div>
  )
}
